import { useCallback, useEffect, useMemo, useState } from 'react'
import {
   Breadcrumb,
   Button,
   Card,
   Col,
   Form,
   Input,
   notification,
   Row,
   Select,
   Typography,
   DatePicker,
   Radio,
   InputNumber,
   AutoComplete,
   Space,
   message,
   Modal
} from 'antd'
import { HomeOutlined, LeftCircleOutlined, PlusCircleOutlined, EditOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import _, { debounce } from 'lodash'
import FloatLabel from 'src/common/components/float-label'
import { useTranslation } from 'react-i18next'
import i18n from 'src/helpers/i18n'
import { createValidationRules } from 'src/helpers/validateRule'
import { useLoading } from 'src/common/components/LoadingContainer'
import { TYPE_BOOKING } from 'src/helpers/constants'
import { useStore } from 'src/hooks'
import TextArea from 'antd/lib/input/TextArea'
import moment from 'moment'

const { Title } = Typography
const { Option } = Select

const TripCreateScreen = observer(() => {
   const history = useHistory()
   const [form] = Form.useForm()
   const { t } = useTranslation()
   const { id } = useParams<{ id: string }>()
   const { showLoading, hideLoading } = useLoading()
   const [tripType, setTripType] = useState<number>(TYPE_BOOKING[0].value)
   const OrderStore = useStore(`OrderStore`)
   const UserStore = useStore('UserStore')

   const readOnly = useMemo(() => {
      if (history.location.pathname.includes('detail')) {
         return true
      } else {
         return false
      }
   }, [history.location])

   const rule = useMemo(() => createValidationRules(), [i18n.language])

   // FETCH DATA & GET DETAIL
   useEffect(() => {
      if (id && id !== 'create') {
         handleGetDetail()
      }
   }, [id])

   const fetchData = async () => {
      await OrderStore.getOrders()
      await OrderStore.getRoutes()
      await UserStore.getEmployees()
   }

   useEffect(() => {
      fetchData()
   }, [OrderStore])

   const handleGetDetail = useCallback(async () => {
      if (!id) return
      try {
         showLoading()
         const res = await OrderStore.getTripDetail(id)
         if (res?.data) {
            const raw = res.data
            const fixedValues = {
               ...raw,
               departureDateTime: raw.departureDateTime ? moment(raw.departureDateTime) : null,
               returnDateTime: raw.returnDateTime ? moment(raw.returnDateTime) : null
            }

            form.setFieldsValue({
               ...fixedValues,
               customerName: fixedValues.customer.name,
               customerPhone: fixedValues.customer.phoneNumber
            })
            setTripType(OrderStore.getDetailTripType(raw?.orderType))
         }
      } finally {
         hideLoading()
      }
   }, [])

   // SUBMIT FORM
   const onFinish = async (values: any) => {
      console.log('Values : ', values)
      const body: any = {
         pickupLocationLat: '0',
         pickupLocationLng: '0',
         dropoffLocationLat: '0',
         dropoffLocationLng: '0',
         promotionCode: '',
         promotionDiscount: 0,
         returnDateTime: values.returnDateTime || null,
         passengerCount: 1,
         ...values
      }
      const data = id ? await OrderStore.updateOrder(body, Number(id)) : await OrderStore.createOrder(body)

      if (data) {
         notification.open({
            message: t('notification'),
            description: id ? t('update_successfully') : t('create_successfully'),
            type: 'success',
            placement: 'topRight'
         })
         history.goBack()
      }
   }

   // HANDLE PHONE SELECTED
   const [options, setOptions] = useState([])
   // phoneOptions
   useEffect(() => {
      const tempArray = []
      // if (OrderStore.listOrder?.rows?.length) {
      //    const filtered = OrderStore.listOrder.rows.map((element: any) => {
      //       return element
      //    })
      //    tempArray.push(...filtered)
      // }
      if (UserStore.listEmployees?.rows?.length) {
         const filtered = UserStore.listEmployees.rows
            .filter((element: any) => element.role === 'user')
            .map((element: any) => {
               return { ...element, value: element.phoneNumber }
            })

         setOptions(filtered)
      }
   }, [OrderStore.listOrder, UserStore.listEmployees])

   const [isDisable, setIsDisable] = useState<boolean>(false)
   const handlePhoneChange = (value: string) => {
      OrderStore.openDropDown = value.length >= 5 ? true : false
      setIsDisable(false)
      const existing = options.find((opt: any) => opt.phoneNumber === value) as any
      if (existing) {
         form.setFieldsValue({
            customerName: existing.name,
            customerId: existing.id
         })
         setIsDisable(true)
      } else {
         form.setFieldsValue({
            customerName: '',
            customerId: null
         })
      }
   }

   const handleSelectPhone = (value: string, option: any) => {
      console.log('option : ', option)

      form.setFieldsValue({
         customerName: option.name,
         customerId: option.id
      })
      OrderStore.openDropDown = false
   }

   // VALIDATE FORM DATE
   const validateDepartureDate = (_: any, value: string) => {
      if (!value) return Promise.reject(new Error('Vui lòng nhập dữ liệu'))
      if (moment(value).isBefore(new Date())) return Promise.reject(new Error('Không được chọn ngày giờ trong quá khứ'))
      return Promise.resolve()
   }

   const validateReturnDate = (_: any, value: string) => {
      const departureDate = form.getFieldValue('departureDateTime')
      if (!value) return Promise.reject(new Error('Vui lòng nhập dữ liệu'))
      if (moment(value).isBefore(new Date())) return Promise.reject(new Error('Không được chọn ngày giờ trong quá khứ'))
      if (moment(value).isBefore(departureDate))
         return Promise.reject(new Error('Không được chọn ngày giờ nhỏ hơn ngày đi'))
      return Promise.resolve()
   }

   // package content
   const packageContent = () => {
      return (
         <>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Số điện thoại người gửi'} required>
                     <Form.Item name="customerPhone" rules={[rule.requiredEnter]}>
                        <AutoComplete
                           open={OrderStore.openDropDown}
                           placeholder={t('enter')}
                           options={options}
                           onChange={handlePhoneChange}
                           onSelect={(value, option) => handleSelectPhone(value, option)}
                           filterOption={(input, option) =>
                              String(option?.value ?? '')
                                 .toLowerCase()
                                 .includes(input.toLowerCase())
                           }
                        />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Họ tên người gửi'} required>
                     <Form.Item name="customerName" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} readOnly={readOnly} disabled={isDisable} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Form.Item name="customerId" hidden>
                  <Input />
               </Form.Item>
            </Row>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Số điện thoại người nhận'} required>
                     <Form.Item name="receiverPhone" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} readOnly={readOnly} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Họ tên người nhận'} required>
                     <Form.Item name="receiverName" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} readOnly={readOnly} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Tuyến đường'} required>
                     <Form.Item name="routeId" rules={[rule.requiredEnter]}>
                        <Select placeholder={t('select')}>
                           {OrderStore.listRoutes?.rows?.map((route: any) => (
                              <Option key={route.id} value={route.id}>
                                 {route.name ?? `${route.fromDistrict?.name} - ${route.toDistrict?.name}`}
                              </Option>
                           ))}
                        </Select>
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Loại dịch vụ'} required>
                     <Form.Item name="orderType" rules={[rule.requiredEnter]}>
                        <Select placeholder={t('select')}>
                           <Option value={6}>Gửi hàng to</Option>
                           <Option value={7}>Gửi hàng nhỏ</Option>
                        </Select>
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Điểm gửi hàng'} required>
                     <Form.Item name="pickupLocationAddress" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} readOnly={readOnly} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Điểm trả hàng'} required>
                     <Form.Item name="dropoffLocationAddress" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} readOnly={readOnly} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Ngày gửi hàng'} required>
                     <Form.Item name="departureDateTime" rules={[rule.requiredEnter]}>
                        <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               {/* <Col xs={24} md={12}>
                  <FloatLabel label={'Kích thước gói hàng'} required>
                     <Form.Item name="packageDimensions" rules={[rule.requiredEnter]}>
                        <InputNumber placeholder={t('enter')} min={0.1} max={50} />
                     </Form.Item>
                  </FloatLabel>
               </Col> */}
            </Row>
            {/* <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Ghi chú đơn hàng'} required>
                     <Form.Item name="packageDescription">
                        <TextArea placeholder={t('package_description')} autoSize={{ minRows: 3, maxRows: 5 }} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row> */}
         </>
      )
   }

   // trip content
   const tripContent = () => {
      return (
         <>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={8}>
                  <FloatLabel label={'Khứ hồi'} required>
                     <Form.Item
                        name="tripDirection"
                        rules={[rule.requiredEnter]}
                        initialValue={OrderStore.selectedTrip}
                     >
                        <Select
                           placeholder={t('select')}
                           onChange={(value: number) => (OrderStore.selectedTrip = value)}
                           defaultValue={OrderStore.selectedTrip}
                        >
                           {id ? (
                              <>
                                 <Option value={1}>Chiều đi</Option>
                                 <Option value={2}>Chiều về</Option>
                              </>
                           ) : (
                              <>
                                 <Option value={1}>Một chiểu</Option>
                                 <Option value={3}>Khứ hồi</Option>
                              </>
                           )}
                        </Select>
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={8}>
                  <FloatLabel label={'Số điện thoại khách'} required>
                     <Form.Item name="customerPhone" rules={[rule.requiredEnter]}>
                        <AutoComplete
                           open={OrderStore.openDropDown}
                           placeholder={t('enter')}
                           options={options}
                           onChange={handlePhoneChange}
                           onSelect={(value, option) => handleSelectPhone(value, option)}
                           filterOption={(input, option) =>
                              String(option?.phoneNumber ?? '')
                                 .toLowerCase()
                                 .includes(input.toLowerCase())
                           }
                        />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={8}>
                  <FloatLabel label={'Họ tên khách'} required>
                     <Form.Item name="customerName" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Form.Item name="customerId" hidden>
                  <Input />
               </Form.Item>
            </Row>

            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Tuyến đường'} required>
                     <Form.Item name="routeId" rules={[rule.requiredEnter]}>
                        <Select
                           placeholder={t('enter')}
                           showSearch
                           filterOption={(input, option) =>
                              String(option?.label ?? '')
                                 .toLowerCase()
                                 .includes(input.toLowerCase())
                           }
                           options={OrderStore.routerOptions()}
                        />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Loại dịch vụ'} required>
                     <Form.Item name="orderType" rules={[rule.requiredEnter]}>
                        <Select placeholder={t('select')}>
                           {tripType == 0 ? (
                              <>
                                 {OrderStore.selectedTrip === 1 ? (
                                    <Option value={1}>Xe ghép</Option>
                                 ) : (
                                    <>
                                       <Option value={4}>Khứ hồi 5 chỗ</Option>
                                       <Option value={5}>Khứ hồi 7 chỗ</Option>
                                    </>
                                 )}
                              </>
                           ) : (
                              <>
                                 <Option value={2}>Bao xe 5 chỗ</Option>
                                 <Option value={3}>Bao xe 7 chỗ</Option>
                              </>
                           )}
                        </Select>
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Điểm đón'} required>
                     <Form.Item name="pickupLocationAddress" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Điểm trả'} required>
                     <Form.Item name="dropoffLocationAddress" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Ngày đi'} required>
                     <Form.Item
                        name="departureDateTime"
                        rules={[
                           {
                              validator: validateDepartureDate
                           }
                        ]}
                     >
                        <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               {OrderStore.selectedTrip === 3 && (
                  <Col xs={24} md={12}>
                     <FloatLabel label={'Ngày về'} required>
                        <Form.Item
                           name="returnDateTime"
                           rules={[
                              {
                                 validator: validateReturnDate
                              }
                           ]}
                        >
                           <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                     </FloatLabel>
                  </Col>
               )}
            </Row>
         </>
      )
   }

   // RENDER FORM CONTENT
   const renderContent = () => (
      <div>
         <Card style={{ width: '100%', marginTop: 20 }}>
            <Row gutter={12} className="gx-flex-row">
               <Col xs={12} md={12}>
                  <Form.Item initialValue={TYPE_BOOKING[0].value}>
                     <Radio.Group
                        value={tripType}
                        options={TYPE_BOOKING}
                        buttonStyle="solid"
                        optionType="button"
                        onChange={(e) => {
                           setTripType(e.target.value)
                           form.setFieldsValue({ orderType: undefined })
                        }}
                     />
                  </Form.Item>
               </Col>
            </Row>
            {tripType != 2 ? tripContent() : packageContent()}

            {/* <Row gutter={12} className="gx-flex-row">
               <Col xs={12} md={12}>
                  <FloatLabel label={'Người bán chuyến'} required>
                     <Form.Item name="seller" rules={[rule.requiredEnter]}>
                        <Input placeholder={t('enter')} readOnly={readOnly} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={12} md={12}>
                  <FloatLabel label={'Tài xế'}>
                     <Form.Item name="driver">
                        <Select placeholder={'Chọn tài xế'} >
                           <Option value="Nguyễn Văn A">Nguyễn Văn A</Option>
                           <Option value="Nguyễn Văn B">Nguyễn Văn B</Option>
                        </Select>
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>

            <Row gutter={12} className="gx-flex-row">
               <Col xs={12} md={12}>
                  <FloatLabel label={'Hoa hồng'}>
                     <Form.Item name="commission">
                        <InputNumber
                           style={{ width: '100%' }}
                           min={0}
                           formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                           parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                           readOnly={readOnly}
                        />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row> */}
         </Card>
      </div>
   )

   return (
      <div>
         <Row style={{ padding: '0 20px' }} className="gx-justify-content-between gx-align-items-center">
            <Col>
               <Title level={4}>{'Quản lý chuyến'}</Title>
               <Breadcrumb>
                  <Breadcrumb.Item href="/">
                     <HomeOutlined className="gx-link" />
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{'Admin'}</Breadcrumb.Item>
                  <Breadcrumb.Item onClick={() => history.goBack()} className="gx-link">
                     {'Quản lý chuyến'}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{id ? (readOnly ? t('details') : t('edit')) : t('add_new')}</Breadcrumb.Item>
                  {id && <Breadcrumb.Item>{id}</Breadcrumb.Item>}
               </Breadcrumb>
            </Col>
         </Row>

         <Form form={form} name="trip_form" layout="vertical" onFinish={debounce(onFinish, 600)}>
            {renderContent()}
            <Form.Item style={{ width: '100%', textAlign: 'center' }}>
               <Button
                  icon={<LeftCircleOutlined />}
                  type="dashed"
                  style={{ color: '#ED5C19' }}
                  onClick={() => history.goBack()}
                  className="gx-mr-2"
               >
                  {t('txt_back')}
               </Button>
               <Button
                  icon={id ? <EditOutlined /> : <PlusCircleOutlined />}
                  type="primary"
                  htmlType="submit"
                  className="gx-mr-2"
               >
                  {id ? t('update') : t('create')}
               </Button>
            </Form.Item>
         </Form>
      </div>
   )
})

export default TripCreateScreen
